import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Badge, SegmentedControl, cn, type Facet } from "@fox1ck-org/ui";
import type { Click } from "@gen/tracker/v1/click_pb";
import { ConversionStatus, type Conversion } from "@gen/tracker/v1/conversion_pb";

import { ListView } from "@/components/list-view";
import { CardFacets } from "@/components/campaign/facets";
import { usePatchFilters } from "@/components/campaign/use-patch-filters";
import { ClickDetailDrawer } from "@/components/canonical/click-detail-drawer";
import { ConversionDetailDrawer } from "@/components/canonical/conversion-detail-drawer";
import { ConversionStatusBadge } from "@/components/canonical/conversion-status-badge";
import { useTableParams } from "@/lib/hooks/use-table-params";
import { useT } from "@/lib/i18n";
import { DASH } from "@/lib/format";
import { formatStamp } from "@/lib/utils";
import { trafficClassLabel, trafficClassTone } from "@/lib/traffic-class";
import { statsRowToNumbers } from "@/lib/services/reporting-schema-service";
import { useCampaignReport } from "@/lib/services/campaign-report-service";
import { useSearchClicks } from "@/lib/services/click-service";
import { useLandersByIds } from "@/lib/services/lander-service";
import type { CardContext } from "./card";

const PAGE_SIZES = [50, 100, 250];
const SEGMENTS = ["all", "people", "bots", "leads"] as const;
type Segment = (typeof SEGMENTS)[number];

/** What a segment asks the server for. Pure. */
export function segmentFilter(s: Segment): { trafficClass?: string; botsOnly?: boolean; withConversions?: boolean } {
  if (s === "people") return { trafficClass: "human" };
  if (s === "bots") return { botsOnly: true };
  if (s === "leads") return { withConversions: true };
  return {};
}

/**
 * Клики кампании (доска CClicks): every visit of the period, newest first,
 * split «Все · Люди · Боты · С лидом» with the server's counts; the city, the
 * device, the class, where the visit went and what came of it. A row opens
 * the click beside the table (шторка) — the table narrows, it is not covered,
 * and ↑ ↓ walk the rows. The first page refreshes itself: clicks arrive live.
 */
export function ClicksTab({ ctx }: { ctx: CardContext }) {
  const t = useT();
  const params = useTableParams({ defaultPageSize: 50, pageSizeOptions: PAGE_SIZES });
  const patch = usePatchFilters();
  const segment = (SEGMENTS as readonly string[]).includes(params.getFilter("seg")) ? (params.getFilter("seg") as Segment) : "all";
  const city = params.getFilter("city");
  const [clickId, setClickId] = useState<string | undefined>(params.getFilter("click") || undefined);
  const [conversion, setConversion] = useState<Conversion | undefined>();

  const query = useSearchClicks(
    {
      range: { from: params.from, to: params.to, timezone: params.tz },
      campaignId: ctx.campaign.id,
      ...segmentFilter(segment),
      city: city || undefined,
      withSegments: true,
      search: params.search || undefined,
      limit: params.pageSize,
      offset: params.pageIndex * params.pageSize,
    },
    { refetchInterval: params.pageIndex === 0 ? 15_000 : undefined },
  );
  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const seg = query.data?.segments;
  const landers = useLandersByIds(items.map((c) => c.landerId));
  const landerName = useMemo(() => new Map([...(landers.data?.values() ?? [])].map((l) => [l.id, l.name] as const)), [landers.data]);

  // The cities of the period with their visits — the report's numbers.
  const cities = useCampaignReport({ campaignId: ctx.campaign.id, from: params.from, to: params.to, tz: params.tz, basis: params.basis, dims: ["city"] });
  const facets: Facet[] = [
    {
      id: "city",
      label: t("card.conv.city"),
      kind: "options",
      searchable: true,
      loading: cities.isLoading,
      options: (cities.data?.rows ?? [])
        .filter((r) => r.dimensionPath[0])
        .map((r) => ({ value: r.dimensionPath[0]!, label: r.dimensionPath[0]!, count: statsRowToNumbers(r.stats).visits ?? 0 })),
    },
  ];

  const columns = useMemo<ColumnDef<Click>[]>(
    () => [
      {
        id: "ts",
        header: t("cj.col.time"),
        size: 128,
        cell: ({ row }) => <span className="whitespace-nowrap font-mono text-xs tabular-nums">{formatStamp(row.original.ts, params.tz, { clock: true })}</span>,
      },
      {
        id: "city",
        header: t("card.conv.city"),
        size: 112,
        cell: ({ row }) => <span className="block truncate">{row.original.city || row.original.country || DASH}</span>,
      },
      {
        id: "device",
        header: t("card.clicks.device"),
        cell: ({ row }) => (
          <span className="block truncate">
            {row.original.os || row.original.device}
            {row.original.browser ? <span className="text-muted-foreground"> · {row.original.browser}</span> : null}
          </span>
        ),
      },
      {
        id: "class",
        header: t("card.clicks.class"),
        size: 104,
        cell: ({ row }) => {
          const c = row.original.trafficClass || (row.original.botFlag ? "bot" : "human");
          const tone = trafficClassTone(c);
          return <Badge variant={tone === "warn" || tone === "danger" || tone === "ok" || tone === "info" ? tone : "neutral"}>{trafficClassLabel(t, c).toLowerCase()}</Badge>;
        },
      },
      {
        id: "lander",
        header: t("card.clicks.went"),
        size: 172,
        cell: ({ row }) => {
          const id = row.original.landerId;
          const name = id ? landerName.get(id) : undefined;
          return <span className={cn("block truncate", !name && "text-muted-foreground")}>{name ?? (row.original.pageServed || DASH)}</span>;
        },
      },
      {
        id: "result",
        header: t("card.clicks.result"),
        size: 96,
        cell: ({ row }) =>
          row.original.conversionStatus !== ConversionStatus.UNSPECIFIED ? <ConversionStatusBadge status={row.original.conversionStatus} /> : <span className="text-muted-foreground">{DASH}</span>,
      },
    ],
    [t, params.tz, landerName],
  );

  const options = SEGMENTS.map((s) => ({
    value: s,
    label: t(`card.clicks.seg.${s}`),
    count: seg ? (s === "all" ? seg.visits : s === "people" ? seg.people : s === "bots" ? seg.bots : seg.withConversions) : undefined,
  }));
  const index = clickId ? items.findIndex((c) => c.clickId === clickId) : -1;

  return (
    <div className={cn("flex min-h-0 flex-1 flex-col gap-3 transition-[padding] duration-[var(--dur-fast)]", clickId && "xl:pr-132")}>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <SegmentedControl size="sm" aria-label={t("card.clicks.segments")} value={segment} onValueChange={(v: Segment) => patch({ seg: v === "all" ? null : v })} options={options} />
        <CardFacets facets={facets} value={{ city }} onChange={patch} />
        <div className="flex-1" />
        <span className="text-xs text-muted-foreground">{params.pageIndex === 0 ? t("card.clicks.live") : t("card.clicks.newest")}</span>
      </div>
      <ListView
        failure={query.error ? { title: t("cj.error"), error: query.error, retry: () => void query.refetch() } : undefined}
        columns={columns}
        data={items}
        getRowId={(c) => c.clickId}
        loading={query.isLoading}
        manualSorting
        density="compact"
        rowClassName={(c) => (c.clickId === clickId ? "bg-primary-soft" : undefined)}
        pagination={{
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
          totalCount: query.data?.total ?? 0,
          onPageIndexChange: params.setPageIndex,
          onPageSizeChange: params.setPageSize,
          pageSizeOptions: PAGE_SIZES,
        }}
        onRowClick={(c) => setClickId(c.clickId)}
        emptyMessage={t("card.clicks.empty")}
      />
      <ClickDetailDrawer
        clickId={clickId}
        open={!!clickId}
        onOpenChange={(o) => !o && setClickId(undefined)}
        onOpenConversion={(c) => {
          setClickId(undefined);
          setConversion(c);
        }}
        onPrev={index > 0 ? () => setClickId(items[index - 1]!.clickId) : undefined}
        onNext={index >= 0 && index < items.length - 1 ? () => setClickId(items[index + 1]!.clickId) : undefined}
      />
      <ConversionDetailDrawer
        conversion={conversion}
        open={!!conversion}
        onOpenChange={(o) => !o && setConversion(undefined)}
        onOpenClick={(id) => {
          setConversion(undefined);
          setClickId(id);
        }}
      />
    </div>
  );
}
